import type { Board, HandSettledPayload, LineResult, Row, Seat, SeatBreakdown } from './protocol';

/**
 * IL RESOCONTO DELLA MANO (fase 2 del tavolo, tavole 4, 5 e 8 della scheda `services/qchinesepoker/ui/2026-10-17-tavolo-premium-ofc.md`): cio' che il tavolo dice
 * quando `hand:settled` arriva. LEGGE e non ricalcola: ogni numero che si mostra e un campo di `breakdown` (esito per fila, scoop, foul, royalty PAGATE) o il
 * netto della mano che il server manda (`netA`/`netB`). Le due sole cose che questo file sa delle regole sono le due COSTANTI che dividono il netto in voci
 * (`BONUS_SCOOP` e `PENALE_FOUL`, QChinesePoker `qcp-rules/src/scoring.ts` @ 9e799e55): servono a scrivere «+3 righe, +3 scoop, +25 royalty», mai a dire
 * quanto vale la mano.
 *
 * ⛔ Il totale e SEMPRE quello del server. Se le voci scomposte con le due costanti non tornano al totale (le regole sono cambiate, o la mano e di un
 * server che non conosciamo) `voci` e `null`: il resoconto mostra gli esiti per fila e il totale, e NON mostra una somma che il server non ha fatto.
 * ⛔ Un `breakdown` assente o malformato non e un errore: `leggiBreakdown` torna `null` e la mano si riassume col solo netto (e le plance, se rivelate).
 */

/** Il bonus dello scoop (3 righe su 3, senza foul): +3 oltre alle tre righe (scoring.ts: somma 3 → 6). */
export const BONUS_SCOOP = 3;
/** La penale di un foul: 6 punti oltre alle royalty dell'altro (scoring.ts: `-6 - B.royalties.total`). */
export const PENALE_FOUL = 6;

export const FILE: readonly Row[] = ['front', 'middle', 'back'];

const ESITI: readonly unknown[] = [-1, 0, 1];
const intero = (n: unknown): n is number => typeof n === 'number' && Number.isInteger(n);

function leggiLato(raw: unknown): SeatBreakdown | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.foul !== 'boolean' || typeof r.scoop !== 'boolean') return null;
  const roy = r.royalties as Record<string, unknown> | null | undefined;
  if (typeof roy !== 'object' || roy === null || !intero(roy.front) || !intero(roy.middle) || !intero(roy.back) || !intero(roy.total)) return null;
  const lato: SeatBreakdown = { foul: r.foul, scoop: r.scoop, royalties: { front: roy.front, middle: roy.middle, back: roy.back, total: roy.total } };
  if (r.lines !== undefined && r.lines !== null) {
    const l = r.lines as Record<string, unknown>;
    if (typeof l !== 'object' || !ESITI.includes(l.front) || !ESITI.includes(l.middle) || !ESITI.includes(l.back)) return null;
    lato.lines = { front: l.front as LineResult, middle: l.middle as LineResult, back: l.back as LineResult };
  }
  return lato;
}

/**
 * Legge `breakdown` con tolleranza: tutte e due le parti, ognuna col suo `foul`, `scoop` e `royalties` interi, e `lines` o assente o con tre esiti in
 * {-1, 0, 1}. Qualunque altra cosa (assente, `null`, una parte sola, un tipo sbagliato) vale `null` = «niente scomposizione», mai un'eccezione.
 */
export function leggiBreakdown(raw: unknown): Record<Seat, SeatBreakdown> | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const A = leggiLato(r.A);
  const B = leggiLato(r.B);
  return A && B ? { A, B } : null;
}

export type ChiaveVoce = 'righeVinte' | 'scoop' | 'scoopAvversario' | 'foulAvversario' | 'tuoFoul' | 'foulEntrambi' | 'tueRoyalty' | 'royaltyAvversario';
export interface VoceResoconto {
  chiave: ChiaveVoce;
  valore: number;
  /** vera quando la voce vale 0 perche chi la dovrebbe avere ha fatto foul: «royalty dell'avversario (foul: nessuna)» */
  perFoul?: boolean;
}

export interface LatoResoconto {
  seat: Seat;
  foul: boolean;
  scoop: boolean;
  /** l'esito di ogni fila contro l'altro lato; `null` se il server non lo manda (foul di uno dei due) */
  esiti: Record<Row, LineResult | null>;
  royalty: Record<Row, number>;
  royaltyTotale: number;
}

export interface Resoconto {
  mano: number;
  /** il netto della mano per ME, come lo dice il server */
  netto: number;
  io: LatoResoconto | null;
  avversario: LatoResoconto | null;
  /** le voci che scompongono `netto`, o `null` se non c'e il breakdown o le voci non tornano al totale */
  voci: VoceResoconto[] | null;
  /** il server ha mandato un `breakdown` leggibile */
  haBreakdown: boolean;
  /** le due plance di questa mano, se il server le ha rivelate */
  plance: { io: Board; avversario: Board } | null;
  /** il mio prossimo giro e Fantasyland (dal server: `fantasylandNext`) */
  fantasylandPerMe: boolean;
}

const lato = (seat: Seat, b: SeatBreakdown): LatoResoconto => ({
  seat,
  foul: b.foul,
  scoop: b.scoop,
  esiti: { front: b.lines?.front ?? null, middle: b.lines?.middle ?? null, back: b.lines?.back ?? null },
  royalty: { front: b.royalties.front, middle: b.royalties.middle, back: b.royalties.back },
  royaltyTotale: b.royalties.total,
});

/** Scompone il netto in voci con le due costanti. Il chiamante confronta la somma col netto del server. */
function scomponi(io: SeatBreakdown, avv: SeatBreakdown): VoceResoconto[] {
  if (io.foul && avv.foul) return [{ chiave: 'foulEntrambi', valore: 0 }];
  if (avv.foul) {
    return [
      { chiave: 'foulAvversario', valore: PENALE_FOUL },
      { chiave: 'tueRoyalty', valore: io.royalties.total },
      { chiave: 'royaltyAvversario', valore: 0, perFoul: true },
    ];
  }
  if (io.foul) {
    return [
      { chiave: 'tuoFoul', valore: -PENALE_FOUL },
      { chiave: 'tueRoyalty', valore: 0, perFoul: true },
      { chiave: 'royaltyAvversario', valore: -avv.royalties.total },
    ];
  }
  const l = io.lines;
  const voci: VoceResoconto[] = [{ chiave: 'righeVinte', valore: l ? l.front + l.middle + l.back : 0 }];
  if (io.scoop) voci.push({ chiave: 'scoop', valore: BONUS_SCOOP });
  if (avv.scoop) voci.push({ chiave: 'scoopAvversario', valore: -BONUS_SCOOP });
  voci.push({ chiave: 'tueRoyalty', valore: io.royalties.total }, { chiave: 'royaltyAvversario', valore: -avv.royalties.total });
  return voci;
}

/** Il resoconto della mano `p` dal punto di vista del posto `mioPosto`. Non lancia mai: ogni pezzo che manca vale «non c'e». */
export function resocontoDi(p: HandSettledPayload, mioPosto: Seat): Resoconto {
  const altro: Seat = mioPosto === 'A' ? 'B' : 'A';
  const netto = mioPosto === 'A' ? p.netA : p.netB;
  const bd = leggiBreakdown((p as { breakdown?: unknown }).breakdown);
  let voci: VoceResoconto[] | null = null;
  if (bd) {
    const prova = scomponi(bd[mioPosto], bd[altro]);
    voci = prova.reduce((s, v) => s + v.valore, 0) === netto ? prova : null;
  }
  const rev = p.reveal;
  const plance = rev && rev.boardA && rev.boardB ? { io: mioPosto === 'A' ? rev.boardA : rev.boardB, avversario: mioPosto === 'A' ? rev.boardB : rev.boardA } : null;
  return {
    mano: p.handNumber,
    netto,
    io: bd ? lato(mioPosto, bd[mioPosto]) : null,
    avversario: bd ? lato(altro, bd[altro]) : null,
    voci,
    haBreakdown: bd !== null,
    plance,
    fantasylandPerMe: p.fantasylandNext?.[mioPosto] === true,
  };
}

/** «+24», «-7», «+0»: un numero con il segno, il segno meno vero. */
export const conSegno = (n: number): string => (n > 0 ? `+${n}` : n < 0 ? `−${Math.abs(n)}` : '+0');
